import { FocusEvent, useRef } from 'react';

import { Moment } from 'moment';

import { InputMask } from '../Input.types';
import { NumberFormat, dateRangeFormat, humanDateFormat } from '../numberFormat/NumberFormat';
import { NumberFormatProps } from '../numberFormat/NumberFormat.types';
import { AlphanumericMask } from '../textMask/AlphanumericMask';
import { CorporateCodeMask } from '../textMask/CorporateCodeMask';
import { DateMask } from '../textMask/DateMask';
import { DollarAmountMask } from '../textMask/DollarAmountMask';
import { PhoneMask } from '../textMask/PhoneMask';
import { PostalMask } from '../textMask/PostalMask';
import { TextMaskProps } from '../textMask/TextMaskProps.types';
import { TimeMask } from '../textMask/TimeMask';

type MaskOptions = {
  dateMaskOptions?: {
    currentDateTime?: string;
    minDate?: Moment;
    maxDate?: Moment;
    shouldSkipBlurValidation?: (e: FocusEvent<HTMLInputElement>) => boolean;
  };
};

/**
 * Two decimals that show a negative value but never let a minus sign be introduced.
 * Entry only — what may be submitted is the caller's business.
 *
 * It keeps a flag rather than reading the value because deleting the last digit of "-2"
 * leaves a lone "-": not a number, so the value reads empty while the field still shows
 * the sign, and every digit typed to replace what was deleted would be refused. Wiping
 * the remnant is not an option either — react-number-format re-reads its value prop
 * while focused only when the float changes, and "-" and "" are both NaN to it — so the
 * sign leaves with one more backspace.
 */
const TwoDecimalNegativeDisplay = (props: NumberFormatProps) => {
  const shown = String(props.value ?? '');
  const isNegative = useRef(shown.startsWith('-'));

  // Values set from outside (a stepper click, a reset, a new row) never reach the guard,
  // so the sign is re-read from any of them that is a number. Mid-edit states are the
  // unparseable ones, and they leave the flag alone.
  if (shown !== '' && Number.isFinite(Number(shown))) {
    isNegative.current = shown.startsWith('-');
  }

  return NumberFormat({
    ...props,
    decimalScale: 2,
    allowNegative: true,
    isAllowed: (values: { value?: string }) => {
      const negative = !!values.value?.startsWith('-');

      if (negative && !isNegative.current) {
        return false;
      }

      isNegative.current = negative;

      return true;
    },
  });
};

export const Mask = (mask: InputMask, options: MaskOptions = {}) => {
  const { dateMaskOptions } = options;

  if (Array.isArray(mask)) {
    return (props: TextMaskProps) =>
      AlphanumericMask({
        ...props,
        allowDigits: mask.includes('digits'),
        allowLetters: mask.includes('letters'),
        allowSpace: mask.includes('space'),
        allowDash: mask.includes('dash'),
        allowUnderscroe: mask.includes('underscore'),
      });
  }

  switch (mask) {
    case 'postal':
      return (props: TextMaskProps) => PostalMask(props);
    case 'dollars':
      return (props: TextMaskProps) => DollarAmountMask(props);
    case 'phone':
      return (props: TextMaskProps) => PhoneMask(props);
    case 'corporate-code':
      return (props: TextMaskProps) => CorporateCodeMask(props);
    case 'integer':
      return (props: NumberFormatProps) => NumberFormat({ ...props, decimalScale: 0 });
    case 'decimal':
      return (props: NumberFormatProps) => NumberFormat({ ...props, decimalScale: 1 });
    case 'two-decimal':
      return (props: NumberFormatProps) => NumberFormat({ ...props, decimalScale: 2 });
    case 'two-decimal-negative-display':
      return TwoDecimalNegativeDisplay;
    case 'positive-integer':
      return (props: NumberFormatProps) => NumberFormat({ ...props, decimalScale: 0, allowNegative: false });
    case 'positive-integer-with-zero':
      return (props: NumberFormatProps) =>
        NumberFormat({ ...props, decimalScale: 0, allowNegative: false, allowLeadingZeros: true });
    case 'positive-decimal':
      return (props: NumberFormatProps) => NumberFormat({ ...props, decimalScale: 1, allowNegative: false });
    case 'positive-two-decimal':
      return (props: NumberFormatProps) => NumberFormat({ ...props, decimalScale: 2, allowNegative: false });
    case 'positive-two-decimal-percent':
      return (props: NumberFormatProps) =>
        NumberFormat({
          ...props,
          decimalScale: 2,
          allowNegative: false,
          suffix: Number(props.value) > 0 ? '%' : undefined,
          isAllowed: (values: { floatValue?: number }) => (values.floatValue ?? 0) <= 100,
        });
    case 'date':
      return (props: TextMaskProps) =>
        DateMask({
          ...props,
          minDate: dateMaskOptions?.minDate,
          maxDate: dateMaskOptions?.maxDate,
          shouldSkipBlurValidation: dateMaskOptions?.shouldSkipBlurValidation,
        });
    case 'date-range':
      return (props: NumberFormatProps) => NumberFormat({ ...props, format: dateRangeFormat });
    /* For now, human-date mask works only for inputs connected with <Controller /> (react-hook-form) component,
      cause onChange prop shouldn't be passed, that's because in date input, user cannot change value from keyboard,
      only by setting it manually by setValue (react-hook-form) */
    case 'human-date':
      return (props: NumberFormatProps) =>
        NumberFormat({ ...props, isNumericString: true, format: humanDateFormat(dateMaskOptions?.currentDateTime) });
    case 'time':
      return (props: TextMaskProps) => TimeMask(props);
    case 'time24':
      return (props: TextMaskProps) => TimeMask({ ...props, is24HourFormat: true });
  }
};
